import { useEffect, useState } from 'react';
import { Squares2X2Icon, SquaresPlusIcon } from '@heroicons/react/24/outline';
import BasicModal from '../../components/modals/BasicModal';
import {
  getSharedTemplate,
  saveAllSharedTemplates,
  saveSharedTemplate,
} from '../../services/UserPacketTemplateService';
import { Mixpanel } from '../../services/Mixpanel';
import { useTutorialContext } from '../Base/Context/TutorialContext';

export const ReviewSharedTemplateModal = ({ templateIds, onClose, onSave, onUse }) => {
  const [templates, setTemplates] = useState([]);
  const [userOwnsTemplate, setUserOwnsTemplate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSavedAll, setHasSavedAll] = useState(false);
  const [error, setError] = useState(null);

  const { showTutorial } = useTutorialContext();

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        setIsLoading(true);
        const settled = await Promise.allSettled(
          templateIds.map(async (id) => {
            const response = await getSharedTemplate(id);
            const isOwner = !!response.data.results.user_owns_template;
            const templateData = response.data.results.template;
            templateData.isOwner = isOwner;
            Mixpanel.track('Shared Template Preview Opened', {
              template_id: id,
              template_title: templateData.title,
              user_owns_template: isOwner,
            });
            return {
              templateData,
              isOwner,
            };
          })
        );
        const templates = settled.filter((r) => r.status === 'fulfilled').map((r) => r.value);
        if (templates.length === 0) {
          throw new Error();
        }
        setUserOwnsTemplate(templates.every((t) => t.isOwner));
        setTemplates(templates.map((t) => t.templateData));
      } catch (err) {
        setError('This template is no longer available.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchTemplate();
  }, [templateIds]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (templates.length > 1) {
        const response = await saveAllSharedTemplates(templates.map((t) => t.id));
        onSave(null);
        Mixpanel.track('Shared Template Saved', {
          template_ids: response.data.results.map((t) => t.id),
          template_count: response.data.results.length,
        });
        setHasSavedAll(true);
        setTemplates(response.data.results.map((t) => ({ ...t, isOwner: true })));
      } else {
        const template = templates[0];
        const response = await saveSharedTemplate(template.id);
        onSave(response.data.results);
        Mixpanel.track('Shared Template Saved', {
          template_id: template.id,
          template_title: template.title,
        });
      }
    } catch (err) {
      setError('Failed to save template. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const showTemplatesTip = () =>
    showTutorial({
      type: 'Saved Templates Tip',
      steps: [
        {
          targetElementTutorialId: 'templates-sidebar',
          header: 'You can find your templates here.',
          alignLeft: true,
        },
      ],
      currentStep: 0,
    });

  const handleUse = (template) => {
    Mixpanel.track('Shared Template Applied By Owner', {
      template_id: template.id,
      template_title: template.title,
    });
    onUse(template);
    if (hasSavedAll) {
      showTemplatesTip();
    }
    onClose();
  };

  const handleClose = () => {
    if (hasSavedAll) {
      Mixpanel.track('Shared Template Preview Closed', {
        template_ids: templateIds,
        user_owns_template: userOwnsTemplate,
      });
      showTemplatesTip();
    } else {
      Mixpanel.track('Shared Template Preview Cancelled', {
        template_ids: templateIds,
        user_owns_template: userOwnsTemplate,
      });
    }
    onClose();
  };

  // Only present the owner framing when there's a template to act on — otherwise
  // the "You Own This Template" header would sit above the unavailable message.
  const showOwnerVariant = userOwnsTemplate;

  if (isLoading) {
    return (
      <BasicModal showModal={true} setShowModal={handleClose} maxWidth='sm:max-w-lg'>
        <div className='py-8 text-center text-sm text-gray-400'>Loading...</div>
      </BasicModal>
    );
  }

  return (
    <BasicModal
      showModal={true}
      setShowModal={handleClose}
      maxWidth='sm:max-w-lg'
      title={
        showOwnerVariant
          ? templates.length === 1
            ? 'You Own This Template'
            : 'You Own These Templates'
          : hasSavedAll
            ? 'Saved!'
            : 'Save to Your Templates'
      }
      icon={
        showOwnerVariant || hasSavedAll ? (
          <Squares2X2Icon className='h-6 w-6 text-emerald-600' />
        ) : (
          <SquaresPlusIcon className='h-6 w-6 text-emerald-600' />
        )
      }>
      {error || templates.length === 0 ? (
        <div className='text-center py-6'>
          <p className='text-sm text-gray-600'>
            {error || 'This template is no longer available.'}
          </p>
          <button
            onClick={handleClose}
            className='mt-4 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500'>
            Close
          </button>
        </div>
      ) : (
        <div className='mt-4'>
          <p className='text-sm text-gray-600'>
            {showOwnerVariant
              ? templates.length === 1
                ? 'This is your own template — nothing to save. Use it to create a new resource with the same structure but different content.'
                : 'These are your own templates — nothing to save. Use one to create a new resource with the same structure but different content.'
              : hasSavedAll
                ? 'You saved these templates, try one out now!'
                : templates.length === 1
                  ? 'A colleague has shared a template with you. Save it to quickly create new resources using the same structure but different content.'
                  : 'Templates turn any topic you’re teaching into a consistent, ready-made set of activities.'}
          </p>

          {templates.map((template) => (
            <div key={template.id} className='border border-gray-200 rounded-lg p-4 mt-2'>
              <div className='flex justify-between w-full items-center'>
                <h3 className='text-lg font-medium text-gray-900 flex items-center gap-2'>
                  <Squares2X2Icon className='h-5 w-5 text-gray-400 shrink-0' />
                  {template.title}
                </h3>
                {template.isOwner && (
                  <button
                    type='button'
                    onClick={() => handleUse(template)}
                    className='px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500'>
                    Use
                  </button>
                )}
              </div>
              {template.description && (
                <p className='text-sm text-gray-600 mt-1'>{template.description}</p>
              )}
            </div>
          ))}

          <div className='mt-6 flex justify-end gap-3'>
            <button
              type='button'
              onClick={handleClose}
              className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500'>
              {showOwnerVariant || hasSavedAll ? 'Close' : 'Cancel'}
            </button>
            {showOwnerVariant || hasSavedAll ? null : (
              <button
                type='button'
                onClick={() => handleSave()}
                disabled={isSaving}
                className='px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed'>
                {isSaving
                  ? 'Saving...'
                  : templates.length === 1
                    ? 'Save Template'
                    : 'Save Templates'}
              </button>
            )}
          </div>
        </div>
      )}
    </BasicModal>
  );
};
